import { useMemo } from 'react';

import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/pages/embryo-details/components/FormField';
import { EmbryoDetailDto } from '@/services/embryo-service';

import { EmbryoFormFieldConfig } from './field-config';
import { useEmbryoFieldPolicy, useEmbryoFieldsEnabled } from './full-inventory';

type EmbryoMeritInfoTabProps = {
  control: Control<EmbryoDetailDto>;
  isBulkMode: boolean;
  columns?: number;
};

export function EmbryoMeritInfoTab({
  control,
  columns = 3,
}: EmbryoMeritInfoTabProps) {
  const { t } = useTranslation();
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const policy = useEmbryoFieldPolicy();

  const fieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'jerseyPtanetMerit',
              label: t('embryo.jerseyPtanetMerit'),
              component: InputNumber,
            },
            {
              name: 'jpi',
              label: t('embryo.jpi'),
              component: InputNumber,
            },
          ]
        : []),
      {
        name: 'holsteinPtanetMerit',
        label: t('embryo.holsteinPtanetMerit'),
        component: InputNumber,
      },
      {
        name: 'tpi',
        label: t('embryo.tpi'),
        component: InputNumber,
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'hornStatus',
              label: t('embryo.hornStatus'),
              component: InputText,
            },
            {
              name: 'betaCasein',
              label: t('embryo.betaCasein'),
              component: InputText,
            },
          ]
        : []),
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'feeder',
              label: t('embryo.feeder'),
              component: InputNumber,
            },
          ]
        : []),
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'ranchValue',
              label: t('embryo.ranchValue'),
              component: InputNumber,
            },
          ]
        : []),
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'profit',
              label: t('embryo.profit'),
              component: InputNumber,
            },
            {
              name: 'ppapy',
              label: t('embryo.ppapy'),
              component: InputNumber,
            },
          ]
        : []),
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'kappaCasein',
              label: t('embryo.kappaCasein'),
              component: InputText,
            },
          ]
        : []),
    ],
    [addRemoveEmbryoFields, t],
  );

  const sireFieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'sireNetMerit',
        label: t('embryo.sireNetMerit'),
        component: InputNumber,
      },
      {
        name: 'donorNetMerit',
        label: t('embryo.donorNetMerit'),
        component: InputNumber,
      },
      {
        name: 'sireCheeseMerit',
        label: t('embryo.sireCheeseMerit'),
        component: InputNumber,
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'donorCheeseMerit',
              label: t('embryo.donorCheeseMerit'),
              component: InputNumber,
            },
          ]
        : []),
      {
        name: 'sireTpi',
        label: t('embryo.sireTpi'),
        component: InputNumber,
      },
      {
        name: 'donorTpi',
        label: t('embryo.donorTpi'),
        component: InputNumber,
      },
      {
        name: 'sireJpi',
        label: t('embryo.sireJpi'),
        component: InputNumber,
      },
      {
        name: 'donorJpi',
        label: t('embryo.donorJpi'),
        component: InputNumber,
      },
    ],
    [addRemoveEmbryoFields, t],
  );

  const allFields = [...fieldConfigs, ...sireFieldConfigs].filter((f) =>
    policy.isVisible(f.name),
  );

  return (
    <div className="merit-info-tab">
      <Fieldset
        pt={{
          root: { className: 'p-2' },
          content: { className: 'space-y-2' },
        }}
      >
        <div
          className="grid gap-x-6 gap-y-1"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {allFields.map((field) => (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              control={control}
              component={field.component}
              disabled={false}
              componentProps={field.componentProps}
            />
          ))}
        </div>
      </Fieldset>
    </div>
  );
}
